import { AfterContentInit, Component, computed, ElementRef, input, output, viewChild } from '@angular/core';
import SignaturePad, { Options, PointGroup } from 'signature_pad';

export type SignaturePadOptions = Options & {
    canvasBackground?: string;
    canvasHeight?: number;
    canvasWidth?: number;
};

@Component({
    template: `<canvas
    #SignatureCanvas
    class="signature-pad-canvas"
    [height]="(options()?.canvasHeight ?? 100) - 2"
    [width]="(options()?.canvasWidth ?? 100) - 2"
    [style.background]="options()?.canvasBackground"></canvas>`,
    selector: 'app-signature-pad',
    styleUrls: ['./signature-pad.component.scss'],
    standalone: true,
    imports: [],
})
export class SignaturePadComponent implements AfterContentInit {
    public options = input<SignaturePadOptions>();
    public drawStart = output<Event>();
    public drawBeforeUpdate = output<Event>();
    public drawAfterUpdate = output<Event>();
    public drawEnd = output<Event>();
    private signaturePad!: SignaturePad;
    canvasElementRef = viewChild.required<ElementRef<HTMLCanvasElement>>('SignatureCanvas');
    myCanvas = computed(() => this.canvasElementRef().nativeElement);

    public ngAfterContentInit(): void {
        this.initSignaturePad(this.myCanvas(), this.options());
    }

    /**
     * Returns signature image as an array of point groups
     */
    public toData(): PointGroup[] {
        if (this.signaturePad) {
            return this.signaturePad.toData();
        } else {
            return [];
        }
    }

    /**
     * Draws signature image from an array of point groups
     */
    public fromData(points: PointGroup[]): void {
        this.signaturePad.fromData(points);
    }

    /**
     * Returns signature image as data URL (see https://mdn.io/todataurl for the list of possible parameters)
     */
    public toDataURL(imageType?: string, quality?: number): string {
        return this.signaturePad.toDataURL(imageType, quality); // save image as data URL
    }

    /**
     * Draws signature image from data URL
     */
    public fromDataURL(
        dataURL: string,
        options: { ratio?: number; width?: number; height?: number } = {}
    ): Promise<void> {
        // set default height and width on read data from URL
        if (!Object.hasOwn(options, 'height') && this.options()?.canvasHeight != null) {
            options.height = this.options()!.canvasHeight;
        }
        if (!Object.hasOwn(options, 'width') && this.options()?.canvasWidth != null) {
            options.width = this.options()!.canvasWidth;
        }
        return this.signaturePad.fromDataURL(dataURL, options);
    }

    /**
     * Clears the canvas
     */
    public clear(): void {
        this.signaturePad.clear();
    }

    /**
     * Returns true if canvas is empty, otherwise returns false
     */
    public isEmpty(): boolean {
        return this.signaturePad.isEmpty();
    }

    /**
     * Unbinds all event handlers
     */
    public off(): void {
        this.signaturePad.off();
    }

    /**
     * Rebinds all event handlers
     */
    public on(): void {
        this.signaturePad.on();
    }

    private initSignaturePad(canvas: HTMLCanvasElement, options?: Options): void {
        this.signaturePad = new SignaturePad(canvas, options);
        this.signaturePad.addEventListener('beginStroke', (event: Event) => this.drawStart.emit(event));
        this.signaturePad.addEventListener('beforeUpdateStroke', (event: Event) => this.drawBeforeUpdate.emit(event));
        this.signaturePad.addEventListener('afterUpdateStroke', (event: Event) => this.drawAfterUpdate.emit(event));
        this.signaturePad.addEventListener('endStroke', (event: Event) => this.drawEnd.emit(event));
        setTimeout(() => this.signaturePad.clear());
    }
}
